/**
 * Utility functions for converting between chart selection names and indexes
 */

/**
 * Convert an array of names to a comma-separated string of indexes
 * @param names - Array of option names
 * @param allOptions - Array of all available options
 * @returns Comma-separated string of indexes (e.g., "0,1,2")
 */
export function namesToIndexes(names: string[], allOptions: string[]): string {
  return names
    .map(name => allOptions.indexOf(name))
    .filter(idx => idx >= 0)
    .join(',')
}

/**
 * Convert a comma-separated string of indexes to an array of names
 * @param indexStr - Comma-separated string of indexes (e.g., "0,1,2")
 * @param allOptions - Array of all available options
 * @returns Array of option names
 */
export function indexesToNames(indexStr: string, allOptions: string[]): string[] {
  if (!indexStr || allOptions.length === 0) return []
  const indexes = indexStr.split(',').map(s => parseInt(s, 10))
  return indexes
    .filter(idx => !isNaN(idx) && idx >= 0 && idx < allOptions.length)
    .map(idx => allOptions[idx])
}

/**
 * Convert a single name to its index as a string
 * @param name - Option name
 * @param allOptions - Array of all available options
 * @returns Index as string (e.g., "0"), or empty string if not found
 */
export function nameToIndex(name: string, allOptions: string[]): string {
  const idx = allOptions.indexOf(name)
  return idx >= 0 ? idx.toString() : ''
}

/**
 * Convert a single index string to its name
 * @param indexStr - Index as string (e.g., "0")
 * @param allOptions - Array of all available options
 * @returns Option name, or undefined if index is invalid
 */
export function indexToName(indexStr: string, allOptions: string[]): string | undefined {
  if (!indexStr || allOptions.length === 0) return undefined
  const idx = parseInt(indexStr, 10)
  if (isNaN(idx) || idx < 0 || idx >= allOptions.length) return undefined
  return allOptions[idx]
}
